import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { getWarehouseDetail, updateWarehouse } from '../../api/warehouse';
import type { WarehouseDetail, WarehouseUpdatePayload } from '../../types/warehouse';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DELIVERY_METHOD_OPTIONS = [
  { label: '包裹', value: 'package' },
  { label: '托盘', value: 'pallet' },
  { label: '货柜', value: 'container' },
];

const COUNTRY_OPTIONS = [
  { label: '中国大陆', value: 'CN' },
  { label: '中国香港', value: 'HK' },
  { label: '美国', value: 'US' },
  { label: '英国', value: 'GB' },
  { label: '德国', value: 'DE' },
  { label: '法国', value: 'FR' },
  { label: '日本', value: 'JP' },
  { label: '韩国', value: 'KR' },
  { label: '澳大利亚', value: 'AU' },
  { label: '加拿大', value: 'CA' },
  { label: '其他', value: 'OT' },
];

const splitMethods = (value?: string) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const WarehouseEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [warehouseName, setWarehouseName] = useState('');
  const { t } = useTranslation();
  const warehouseId = Number(id);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!warehouseId) {
        message.error('仓库ID无效');
        setLoading(false);
        return;
      }

      try {
        const { data } = await getWarehouseDetail(warehouseId);
        const currentPath = window.location.pathname.split('?')[0];
        const detailPath = currentPath.replace(/\/edit$/, '');
        const name = String(data?.name || '');
        setWarehouseName(name);
        if (name) {
          sessionStorage.setItem(`tab_title_warehouse:${detailPath}`, name);
          sessionStorage.setItem(`tab_title_warehouse_edit:${currentPath}`, name);
        }
        form.setFieldsValue({
          ...data,
          inbound_methods: splitMethods(data.inbound_methods),
          outbound_methods: splitMethods(data.outbound_methods),
        });
      } catch (error) {
        message.error('获取仓库详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [warehouseId, form]);

  useEffect(() => {
    const title = warehouseName
      ? `${t('tab.warehousesEdit')}-${warehouseName}`
      : t('tab.warehousesEdit');
    try {
      document.title = title;
    } catch {}
    try {
      const w: any = window as any;
      if (w?.tabsManager && typeof w.tabsManager.updateTabTitle === 'function') {
        w.tabsManager.updateTabTitle(title);
      }
    } catch {}
  }, [warehouseName, t]);

  const handleSave = async (values: Partial<WarehouseDetail>) => {
    if (!warehouseId) return;
    setSaving(true);
    try {
      const currentPath = window.location.pathname;
      const targetPath = `/dashboard/admin/warehouses/${warehouseId}`;
      const payload: WarehouseUpdatePayload = {
        ...values,
        inbound_methods: Array.isArray(values.inbound_methods)
          ? values.inbound_methods.join(',')
          : '',
        outbound_methods: Array.isArray(values.outbound_methods)
          ? values.outbound_methods.join(',')
          : '',
      };
      await updateWarehouse(warehouseId, payload);
      message.success('仓库信息已保存');
      navigate(targetPath, { replace: true });
      const tabsManager = (window as any)?.tabsManager;
      if (tabsManager && typeof tabsManager.closeTabByPath === 'function') {
        setTimeout(() => {
          tabsManager.closeTabByPath(currentPath);
        }, 0);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || '保存失败，请检查输入后重试');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card>
        <div className="mb-4">
          <Title level={4} style={{ margin: 0 }}>
            编辑仓库
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="仓库名称" rules={[{ required: true, message: '请输入仓库名称' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="code" label="仓库代码" rules={[{ required: true, message: '请输入仓库代码' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="country" label="所在国家" rules={[{ required: true, message: '请选择所在国家' }]}>
                <Select placeholder="请选择所在国家">
                  {COUNTRY_OPTIONS.map((it) => (
                    <Option key={it.value} value={it.value}>
                      {it.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company_name" label="公司名称" rules={[{ required: true, message: '请输入公司名称' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="address_line1" label="详细地址第一行" rules={[{ required: true, message: '请输入详细地址第一行' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address_line2" label="详细地址第二行">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="city" label="城市" rules={[{ required: true, message: '请输入城市' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="state" label="州/省" rules={[{ required: true, message: '请输入州/省' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="postal_code" label="邮编" rules={[{ required: true, message: '请输入邮编' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contact_name" label="联系人" rules={[{ required: true, message: '请输入联系人' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contact_phone" label="联系电话" rules={[{ required: true, message: '请输入联系电话' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contact_email" label="联系邮箱" rules={[{ required: true, message: '请输入联系邮箱' }, { type: 'email', message: '邮箱格式不正确' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="working_hours" label="工作时间" rules={[{ required: true, message: '请输入工作时间' }]}>
            <TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="inbound_methods" label="入库方式">
                <Checkbox.Group options={DELIVERY_METHOD_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="outbound_methods" label="出库方式">
                <Checkbox.Group options={DELIVERY_METHOD_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="shipping_channels" label="可发货物流渠道" rules={[{ required: true, message: '请输入可发货物流渠道' }]}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="has_loading_dock" label="是否具备卸货平台" valuePropName="checked">
                <Switch checkedChildren="有" unCheckedChildren="无" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="inbound_appointment_requirements" label="入库预约要求">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="outbound_appointment_requirements" label="出库预约要求">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notes" label="备注信息">
            <TextArea rows={3} />
          </Form.Item>

          <Space>
            <Button onClick={() => navigate(`/dashboard/admin/warehouses/${warehouseId}`, { replace: true })}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              保存
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default WarehouseEdit;
